/**
 * Utility function to translate tree node labels
 * Maps node IDs to translation keys
 */

// Business credit node ID to translation key mapping
const businessNodeTranslations = {
  start: 'knowledgeBase.business.start',
  introduction: 'knowledgeBase.business.introduction',
  phase1: 'knowledgeBase.business.phase1',
  wealthMindset: 'knowledgeBase.business.wealthMindset',
  businessStructures: 'knowledgeBase.business.businessStructures',
  einDunsNaics: 'knowledgeBase.business.einDunsNaics',
  contactDetails: 'knowledgeBase.business.contactDetails',
  bankSetup: 'knowledgeBase.business.bankSetup',
  phase2: 'knowledgeBase.business.phase2',
  creditReportingAgencies: 'knowledgeBase.business.creditReportingAgencies',
  businessCreditScore: 'knowledgeBase.business.businessCreditScore',
  tieredVendorStrategy: 'knowledgeBase.business.tieredVendorStrategy',
  monitoringTools: 'knowledgeBase.business.monitoringTools',
  phase3: 'knowledgeBase.business.phase3',
  typesBusinessCredit: 'knowledgeBase.business.typesBusinessCredit',
  tiersApprovals: 'knowledgeBase.business.tiersApprovals',
  applicationBlueprint: 'knowledgeBase.business.applicationBlueprint',
  phase4: 'knowledgeBase.business.phase4',
  smartUses: 'knowledgeBase.business.smartUses',
  velocityModel: 'knowledgeBase.business.velocityModel',
  creativeLeverage: 'knowledgeBase.business.creativeLeverage',
  phase5: 'knowledgeBase.business.phase5',
  realEstateCredit: 'knowledgeBase.business.realEstateCredit',
  ecommerceDigital: 'knowledgeBase.business.ecommerceDigital',
  multipleEntities: 'knowledgeBase.business.multipleEntities',
  holdingCompanies: 'knowledgeBase.business.holdingCompanies',
  phase6: 'knowledgeBase.business.phase6',
  creditHealth: 'knowledgeBase.business.creditHealth',
  insuranceCompliance: 'knowledgeBase.business.insuranceCompliance',
  recessionProofing: 'knowledgeBase.business.recessionProofing',
  templatesTools: 'knowledgeBase.business.templatesTools',
  llcFormationChecklist: 'knowledgeBase.business.llcFormationChecklist',
  fundabilityChecklist: 'knowledgeBase.business.fundabilityChecklist',
  applicationTemplates: 'knowledgeBase.business.applicationTemplates',
  bankScripts: 'knowledgeBase.business.bankScripts',
  vendorDatabase: 'knowledgeBase.business.vendorDatabase',
  creditSpreadsheet: 'knowledgeBase.business.creditSpreadsheet',
  notionTemplate: 'knowledgeBase.business.notionTemplate',
  executionCalendar: 'knowledgeBase.business.executionCalendar',
  integrationGuide: 'knowledgeBase.business.integrationGuide',
  printableJournal: 'knowledgeBase.business.printableJournal',
  bonusModules: 'knowledgeBase.business.bonusModules',
  aiAutomationTools: 'knowledgeBase.business.aiAutomationTools',
  makeZapierAutomation: 'knowledgeBase.business.makeZapierAutomation',
  personalCreditImprovement: 'knowledgeBase.business.personalCreditImprovement',
  coachingOffer: 'knowledgeBase.business.coachingOffer',
  investorPitchGuide: 'knowledgeBase.business.investorPitchGuide',
  caseStudies: 'knowledgeBase.business.caseStudies',
};

// Client mentor node ID to translation key mapping
const clientMentorNodeTranslations = {
  start: 'knowledgeBase.clientMentor.start',
  clientModeGuide: 'knowledgeBase.clientMentor.clientModeGuide',
};

// Personal credit node ID to translation key mapping
const personalNodeTranslations = {
  start: 'knowledgeBase.personal.start',
  creditFundamentals: 'knowledgeBase.personal.creditFundamentals',
  creditFactors: 'knowledgeBase.personal.creditFactors',
  creditBureaus: 'knowledgeBase.personal.creditBureaus',
  profileStages: 'knowledgeBase.personal.profileStages',
  bankingStrategy: 'knowledgeBase.personal.bankingStrategy',
  bankingTiers: 'knowledgeBase.personal.bankingTiers',
  relationshipBuilding: 'knowledgeBase.personal.relationshipBuilding',
  chaseStrategy: 'knowledgeBase.personal.chaseStrategy',
  applicationTiming: 'knowledgeBase.personal.applicationTiming',
  advancedTechniques: 'knowledgeBase.personal.advancedTechniques',
  zeroInterestStrategies: 'knowledgeBase.personal.zeroInterestStrategies',
  creditStacking: 'knowledgeBase.personal.creditStacking',
  specialistTactics: 'knowledgeBase.personal.specialistTactics',
  advancedApplications: 'knowledgeBase.personal.advancedApplications',
  limitOptimization: 'knowledgeBase.personal.limitOptimization',
  riskManagement: 'knowledgeBase.personal.riskManagement',
  commonMistakes: 'knowledgeBase.personal.commonMistakes',
  debtManagement: 'knowledgeBase.personal.debtManagement',
  avalancheMethod: 'knowledgeBase.personal.avalancheMethod',
  snowballMethod: 'knowledgeBase.personal.snowballMethod',
  hardshipPrograms: 'knowledgeBase.personal.hardshipPrograms',
  creditRepairWorkflow: 'knowledgeBase.personal.creditRepairWorkflow',
  disputeErrors: 'knowledgeBase.personal.disputeErrors',
  removeCollections: 'knowledgeBase.personal.removeCollections',
  personalInfoCleanup: 'knowledgeBase.personal.personalInfoCleanup',
  utilizationResetLoan: 'knowledgeBase.personal.utilizationResetLoan',
  rewardsOptimization: 'knowledgeBase.personal.rewardsOptimization',
  transferPartnerHacking: 'knowledgeBase.personal.transferPartnerHacking',
  cashbackVsTravel: 'knowledgeBase.personal.cashbackVsTravel',
  limitBuilding: 'knowledgeBase.personal.limitBuilding',
  AUTradelines: 'knowledgeBase.personal.AUTradelines',
  SoftPullCLI: 'knowledgeBase.personal.SoftPullCLI',
  ComparableCreditStrategy: 'knowledgeBase.personal.ComparableCreditStrategy',
  debtRepositioning: 'knowledgeBase.personal.debtRepositioning',
  paymentTiming: 'knowledgeBase.personal.paymentTiming',
  avoidTechTradelines: 'knowledgeBase.personal.avoidTechTradelines',
  amexPayOverTimeWarning: 'knowledgeBase.personal.amexPayOverTimeWarning',
};

/**
 * Get translation key for a node ID
 * @param {string} nodeId - The node ID
 * @param {string} treeType - 'business', 'personal', or 'client-mentor'
 * @returns {string|null} Translation key or null if not found
 */
export function getTranslationKey(nodeId, treeType = 'business') {
  if (treeType === 'business') {
    return businessNodeTranslations[nodeId] || null;
  } else if (treeType === 'personal') {
    return personalNodeTranslations[nodeId] || null;
  } else if (treeType === 'client-mentor') {
    return clientMentorNodeTranslations[nodeId] || null;
  }
  return null;
}

/**
 * Translate a node label
 * @param {string} nodeId - The node ID
 * @param {string} originalLabel - Original label as fallback
 * @param {Function} t - Translation function
 * @param {string} treeType - 'business', 'personal', or 'client-mentor'
 * @returns {string} Translated label
 */
export function translateTreeLabel(nodeId, originalLabel, t, treeType = 'business') {
  const translationKey = getTranslationKey(nodeId, treeType);
  if (translationKey) {
    const translated = t(translationKey, originalLabel);
    return translated !== translationKey ? translated : originalLabel;
  }
  return originalLabel;
}

